import { message } from 'antd';
import { isAxiosError } from 'axios';
import qs from 'qs';
import { replace } from 'redux-first-history';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/Contacts/Columns';

import type { Call, Campaign, Contact, Email, Group } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { watchHandleError } from '~/redux/app/saga';

import type { StringKeys } from '~/types/utils';

import type {
  createCallsSelectors,
  createContactsSelectors,
  createEmailsSelectors,
  createSmsSelectors,
} from './selectors';

export function createContactsSaga(
  prefix: string,
  { getColumns, getItem, getParams }: ReturnType<typeof createContactsSelectors>,
): () => Generator<ForkEffect, void> {
  const call: any = Effects.call;

  function* watchAddContactsToCampaign({
    payload: { campaign, lockedFilters },
  }: PayloadAction<{
    campaign: Campaign;
    lockedFilters?: Partial<Record<StringKeys<Contact>, string | number | string[]>>;
  }>): Generator<Effect, void, Contact> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

    const currentParams = yield select(getParams);

    try {
      yield call(Api.addContactsToCampaign, campaign, {
        ...currentParams,
        ...lockedFilters,
      });

      message.success('Contacts ajoutés à la campagne avec succès.');
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchAddContactsToGroup({
    payload: { group, lockedFilters },
  }: PayloadAction<{
    group: Group;
    lockedFilters?: Partial<Record<StringKeys<Contact>, string | number | string[]>>;
  }>): Generator<Effect, void, Contact> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

    const currentParams = yield select(getParams);

    try {
      yield call(Api.addContactsToGroup, group, {
        ...currentParams,
        ...lockedFilters,
      });

      message.success('Contacts ajoutés au groupe avec succès.');

      yield call(watchIndex, { payload: currentParams });
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchIndex({
    payload: params,
  }: PayloadAction<IRestIndexParams<Contact>>): Generator<Effect, void, IndexResponse<Contact>> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

    try {
      const currentParams = yield select(getParams);
      const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/contacts', {
        ...currentParams,
        ...params,
      });

      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setData`, payload: items });
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setPage`, payload: currentPage });
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setPageSize`, payload: pageSize });
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setTotal`, payload: total });
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setTotalPages`, payload: totalPages });
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchLoadParams(): Generator<Effect, void, any> {
    const currentParams = yield select(getParams);
    const params = qs.parse(document.location.search.substring(1));

    if (Object.keys(params).length === 0) {
      return;
    }

    for (const key in params) {
      let value = params[key];
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      if (!value || typeof value !== 'string') {
        continue;
      }

      for (const k in currentParams) {
        switch (k) {
          case 'sortBy':
          case 'page':
          case 'pageSize':
          case 'with':
          case 'columns':
            break;
          default:
            if (!params[k]) {
              yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/unsetFilter`, payload: k });
            }
            break;
        }
      }

      let parsed;
      switch (key) {
        case 'sortBy':
          if (params.sortBy !== currentParams.sortBy) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setSortBy`, payload: params.sortBy });
          }
          break;
        case 'page':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.page) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setPage`, payload: parsed });
          }
          break;
        case 'pageSize':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.pageSize) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setPage`, payload: parsed });
          }
          break;
        case 'columns':
          parsed = value.split(',').filter((v) => !!v);
          if (parsed.length > 0) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}contacts/setColumns`,
              payload: {
                view: 'index',
                columns: parsed,
              },
            });
          }
          break;
        case 'with':
          break;
        default:
          if (typeof value === 'string' && params[key] !== currentParams[key]) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}contacts/setFilter`,
              payload: {
                key: key as keyof Contact,
                value,
              },
            });
          }
          break;
      }
    }
  }

  function* watchRemoveContactsFromCampaign({
    payload: { campaign, lockedFilters },
  }: PayloadAction<{
    campaign: Campaign;
    lockedFilters?: Partial<Record<StringKeys<Contact>, string | number | string[]>>;
  }>): Generator<Effect, void, Contact> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

    const currentParams = yield select(getParams);

    try {
      yield call(Api.removeContactsFromCampaign, campaign, {
        ...currentParams,
        ...lockedFilters,
      });

      yield call(watchIndex, { payload: { campaigns: campaign.id } });

      message.success('Contacts retirés de la campagne avec succès.');
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchRemoveContactsFromGroup({
    payload: { group, lockedFilters },
  }: PayloadAction<{
    group: Group;
    lockedFilters?: Partial<Record<StringKeys<Contact>, string | number | string[]>>;
  }>): Generator<Effect, void, Contact> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

    const currentParams = yield select(getParams);

    try {
      yield call(Api.removeContactsFromGroup, group, {
        ...currentParams,
        ...lockedFilters,
      });

      yield call(watchIndex, { payload: { groups: group.id } });

      message.success('Contacts retirés de la campagne avec succès.');
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchRetrieve({
    payload: { id, params },
  }: PayloadAction<{ id: Contact['id'] | Contact['humanId']; params?: IRestIndexParams<Contact> }>): Generator<
    Effect,
    void,
    Contact
  > {
    try {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: true });
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: null });

      let item = yield select(getItem);
      if (item?.humanId !== id) {
        yield put(app.title('Chargement...'));
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setItem`, payload: null });
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setInitialItem`, payload: null });

        item = yield call(Api.retrieve, `/contacts/${id}`, {
          ...(params ?? {}),
          with: getWithParamString(Columns, 'single'),
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}contacts/setItem`,
          payload: item,
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}contacts/setInitialItem`,
          payload: JSON.stringify(item),
        });
      }

      yield put(app.title(`${item.firstName ?? ''} ${item.lastName ?? ''}`));
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}contacts/setLoading`, payload: false });
    }
  }

  function* watchSetParams({ payload: path }: PayloadAction<string>): Generator<Effect, void, any> {
    const params = yield select(getParams);
    const saveParams: Record<string, any> = {};

    const columns = yield select(getColumns);

    for (const key in params) {
      saveParams[key] = params[key];
    }

    saveParams.columns = columns.join(',');

    yield put(
      replace({
        pathname: path,
        search: qs.stringify(saveParams),
      }),
    );
  }

  function* watchContactsSagas(): Generator<ForkEffect, void> {
    yield debounce(250, `${prefix ? `${prefix}/` : ''}contacts/index`, watchIndex);
    yield debounce(100, `${prefix ? `${prefix}/` : ''}contacts/retrieve`, watchRetrieve);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}contacts/addContactsToCampaign`, watchAddContactsToCampaign);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}contacts/addContactsToGroup`, watchAddContactsToGroup);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}contacts/loadParams`, watchLoadParams);
    yield takeEvery(
      `${prefix ? `${prefix}/` : ''}contacts/removeContactsFromCampaign`,
      watchRemoveContactsFromCampaign,
    );
    yield takeEvery(`${prefix ? `${prefix}/` : ''}contacts/removeContactsFromGroup`, watchRemoveContactsFromGroup);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}contacts/setParams`, watchSetParams);
  }

  return watchContactsSagas;
}

export function createCallsSaga(
  prefix: string,
  { getColumns, getItem, getParams }: ReturnType<typeof createCallsSelectors>,
): () => Generator<ForkEffect, void> {
  const call: any = Effects.call;

  function* watchIndex({
    payload: params,
  }: PayloadAction<IRestIndexParams<Call>>): Generator<Effect, void, IndexResponse<Call>> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setError`, payload: null });

    try {
      const currentParams = yield select(getParams);
      const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/calls', {
        ...currentParams,
        ...params,
      });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setData`, payload: items });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setPage`, payload: currentPage });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setPageSize`, payload: pageSize });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setTotal`, payload: total });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setTotalPages`, payload: totalPages });
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setLoading`, payload: false });
    }
  }

  function* watchLoadParams(): Generator<Effect, void, any> {
    const currentParams = yield select(getParams);
    const params = qs.parse(document.location.search.substring(1));

    if (Object.keys(params).length === 0) {
      return;
    }

    for (const key in params) {
      let value = params[key];
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      if (!value || typeof value !== 'string') {
        continue;
      }

      for (const k in currentParams) {
        switch (k) {
          case 'sortBy':
          case 'page':
          case 'pageSize':
          case 'with':
          case 'columns':
            break;
          default:
            if (!params[k]) {
              yield put({ type: `${prefix ? `${prefix}/` : ''}calls/unsetFilter`, payload: k });
            }
            break;
        }
      }

      let parsed;
      switch (key) {
        case 'sortBy':
          if (params.sortBy !== currentParams.sortBy) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setSortBy`, payload: params.sortBy });
          }
          break;
        case 'page':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.page) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setPage`, payload: parsed });
          }
          break;
        case 'pageSize':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.pageSize) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setPage`, payload: parsed });
          }
          break;
        case 'columns':
          parsed = value.split(',').filter((v) => !!v);
          if (parsed.length > 0) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}calls/setColumns`,
              payload: {
                view: 'index',
                columns: parsed,
              },
            });
          }
          break;
        case 'with':
          break;
        default:
          if (typeof value === 'string' && params[key] !== currentParams[key]) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}calls/setFilter`,
              payload: {
                key: key as keyof Call,
                value,
              },
            });
          }
          break;
      }
    }
  }

  function* watchRetrieve({
    payload: { id, params },
  }: PayloadAction<{ id: Call['id'] | Call['humanId']; params?: IRestIndexParams<Call> }>): Generator<
    Effect,
    void,
    Call
  > {
    try {
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setLoading`, payload: true });
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setError`, payload: null });

      let item = yield select(getItem);
      if (item?.humanId !== id) {
        yield put(app.title('Chargement...'));
        yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setItem`, payload: null });
        yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setInitialItem`, payload: null });

        item = yield call(Api.retrieve, `/calls/${id}`, {
          ...(params ?? {}),
          with: getWithParamString(Columns, 'single'),
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}calls/setItem`,
          payload: item,
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}calls/setInitialItem`,
          payload: JSON.stringify(item),
        });
      }

      yield put(app.title(`Appel ${item.humanId}`));
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}calls/setLoading`, payload: false });
    }
  }

  function* watchSetParams({ payload: path }: PayloadAction<string>): Generator<Effect, void, any> {
    const params = yield select(getParams);
    const saveParams: Record<string, any> = {};

    const columns = yield select(getColumns);

    for (const key in params) {
      saveParams[key] = params[key];
    }

    saveParams.columns = columns.join(',');

    yield put(
      replace({
        pathname: path,
        search: qs.stringify(saveParams),
      }),
    );
  }

  function* watchCallsSagas(): Generator<ForkEffect, void> {
    yield debounce(250, `${prefix ? `${prefix}/` : ''}calls/index`, watchIndex);
    yield debounce(100, `${prefix ? `${prefix}/` : ''}calls/retrieve`, watchRetrieve);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}calls/loadParams`, watchLoadParams);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}calls/setParams`, watchSetParams);
  }

  return watchCallsSagas;
}

export function createEmailsSaga(
  prefix: string,
  { getColumns, getItem, getParams }: ReturnType<typeof createEmailsSelectors>,
): () => Generator<ForkEffect, void> {
  const call: any = Effects.call;

  function* watchIndex({
    payload: params,
  }: PayloadAction<IRestIndexParams<Email>>): Generator<Effect, void, IndexResponse<Email>> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setError`, payload: null });

    try {
      const currentParams = yield select(getParams);
      const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/emails', {
        ...currentParams,
        ...params,
      });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setData`, payload: items });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setPage`, payload: currentPage });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setPageSize`, payload: pageSize });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setTotal`, payload: total });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setTotalPages`, payload: totalPages });
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setLoading`, payload: false });
    }
  }

  function* watchLoadParams(): Generator<Effect, void, any> {
    const currentParams = yield select(getParams);
    const params = qs.parse(document.location.search.substring(1));

    if (Object.keys(params).length === 0) {
      return;
    }

    for (const key in params) {
      let value = params[key];
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      if (!value || typeof value !== 'string') {
        continue;
      }

      for (const k in currentParams) {
        switch (k) {
          case 'sortBy':
          case 'page':
          case 'pageSize':
          case 'with':
          case 'columns':
            break;
          default:
            if (!params[k]) {
              yield put({ type: `${prefix ? `${prefix}/` : ''}emails/unsetFilter`, payload: k });
            }
            break;
        }
      }

      let parsed;
      switch (key) {
        case 'sortBy':
          if (params.sortBy !== currentParams.sortBy) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setSortBy`, payload: params.sortBy });
          }
          break;
        case 'page':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.page) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setPage`, payload: parsed });
          }
          break;
        case 'pageSize':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.pageSize) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setPage`, payload: parsed });
          }
          break;
        case 'columns':
          parsed = value.split(',').filter((v) => !!v);
          if (parsed.length > 0) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}emails/setColumns`,
              payload: {
                view: 'index',
                columns: parsed,
              },
            });
          }
          break;
        case 'with':
          break;
        default:
          if (typeof value === 'string' && params[key] !== currentParams[key]) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}emails/setFilter`,
              payload: {
                key: key as keyof Email,
                value,
              },
            });
          }
          break;
      }
    }
  }

  function* watchRetrieve({
    payload: { id, params },
  }: PayloadAction<{ id: Email['id'] | Email['humanId']; params?: IRestIndexParams<Email> }>): Generator<
    Effect,
    void,
    Email
  > {
    try {
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setLoading`, payload: true });
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setError`, payload: null });

      let item = yield select(getItem);
      if (item?.humanId !== id) {
        yield put(app.title('Chargement...'));
        yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setItem`, payload: null });
        yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setInitialItem`, payload: null });

        item = yield call(Api.retrieve, `/emails/${id}`, {
          ...(params ?? {}),
          with: getWithParamString(Columns, 'single'),
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}emails/setItem`,
          payload: item,
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}emails/setInitialItem`,
          payload: JSON.stringify(item),
        });
      }

      yield put(app.title(`Appel ${item.humanId}`));
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}emails/setLoading`, payload: false });
    }
  }

  function* watchSetParams({ payload: path }: PayloadAction<string>): Generator<Effect, void, any> {
    const params = yield select(getParams);
    const saveParams: Record<string, any> = {};

    const columns = yield select(getColumns);

    for (const key in params) {
      saveParams[key] = params[key];
    }

    saveParams.columns = columns.join(',');

    yield put(
      replace({
        pathname: path,
        search: qs.stringify(saveParams),
      }),
    );
  }

  function* watchEmailsSagas(): Generator<ForkEffect, void> {
    yield debounce(250, `${prefix ? `${prefix}/` : ''}emails/index`, watchIndex);
    yield debounce(100, `${prefix ? `${prefix}/` : ''}emails/retrieve`, watchRetrieve);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}emails/loadParams`, watchLoadParams);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}emails/setParams`, watchSetParams);
  }

  return watchEmailsSagas;
}

export function createSmsSaga(
  prefix: string,
  { getColumns, getItem, getParams }: ReturnType<typeof createSmsSelectors>,
): () => Generator<ForkEffect, void> {
  const call: any = Effects.call;

  function* watchIndex({
    payload: params,
  }: PayloadAction<IRestIndexParams<Email>>): Generator<Effect, void, IndexResponse<Email>> {
    yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setLoading`, payload: true });
    yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setError`, payload: null });

    try {
      const currentParams = yield select(getParams);
      const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/sms', {
        ...currentParams,
        ...params,
      });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setData`, payload: items });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setPage`, payload: currentPage });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setPageSize`, payload: pageSize });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setTotal`, payload: total });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setTotalPages`, payload: totalPages });
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setLoading`, payload: false });
    }
  }

  function* watchLoadParams(): Generator<Effect, void, any> {
    const currentParams = yield select(getParams);
    const params = qs.parse(document.location.search.substring(1));

    if (Object.keys(params).length === 0) {
      return;
    }

    for (const key in params) {
      let value = params[key];
      if (Array.isArray(value)) {
        value = value.join(',');
      }
      if (!value || typeof value !== 'string') {
        continue;
      }

      for (const k in currentParams) {
        switch (k) {
          case 'sortBy':
          case 'page':
          case 'pageSize':
          case 'with':
          case 'columns':
            break;
          default:
            if (!params[k]) {
              yield put({ type: `${prefix ? `${prefix}/` : ''}sms/unsetFilter`, payload: k });
            }
            break;
        }
      }

      let parsed;
      switch (key) {
        case 'sortBy':
          if (params.sortBy !== currentParams.sortBy) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setSortBy`, payload: params.sortBy });
          }
          break;
        case 'page':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.page) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setPage`, payload: parsed });
          }
          break;
        case 'pageSize':
          parsed = Number.parseInt(value, 10);
          if (!Number.isNaN(parsed) && parsed !== currentParams.pageSize) {
            yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setPage`, payload: parsed });
          }
          break;
        case 'columns':
          parsed = value.split(',').filter((v) => !!v);
          if (parsed.length > 0) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}sms/setColumns`,
              payload: {
                view: 'index',
                columns: parsed,
              },
            });
          }
          break;
        case 'with':
          break;
        default:
          if (typeof value === 'string' && params[key] !== currentParams[key]) {
            yield put({
              type: `${prefix ? `${prefix}/` : ''}sms/setFilter`,
              payload: {
                key: key as keyof Email,
                value,
              },
            });
          }
          break;
      }
    }
  }

  function* watchRetrieve({
    payload: { id, params },
  }: PayloadAction<{ id: Email['id'] | Email['humanId']; params?: IRestIndexParams<Email> }>): Generator<
    Effect,
    void,
    Email
  > {
    try {
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setLoading`, payload: true });
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setError`, payload: null });

      let item = yield select(getItem);
      if (item?.humanId !== id) {
        yield put(app.title('Chargement...'));
        yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setItem`, payload: null });
        yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setInitialItem`, payload: null });

        item = yield call(Api.retrieve, `/sms/${id}`, {
          ...(params ?? {}),
          with: getWithParamString(Columns, 'single'),
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}sms/setItem`,
          payload: item,
        });
        yield put({
          type: `${prefix ? `${prefix}/` : ''}sms/setInitialItem`,
          payload: JSON.stringify(item),
        });
      }

      yield put(app.title(`Appel ${item.humanId}`));
    } catch (e) {
      yield call(Api.handleError, e);

      if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
        yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setError`, payload: e.response.data });
        yield call(watchHandleError, e.response.data, Columns);
      }
    } finally {
      yield put({ type: `${prefix ? `${prefix}/` : ''}sms/setLoading`, payload: false });
    }
  }

  function* watchSetParams({ payload: path }: PayloadAction<string>): Generator<Effect, void, any> {
    const params = yield select(getParams);
    const saveParams: Record<string, any> = {};

    const columns = yield select(getColumns);

    for (const key in params) {
      saveParams[key] = params[key];
    }

    saveParams.columns = columns.join(',');

    yield put(
      replace({
        pathname: path,
        search: qs.stringify(saveParams),
      }),
    );
  }

  function* watchSmsSagas(): Generator<ForkEffect, void> {
    yield debounce(250, `${prefix ? `${prefix}/` : ''}sms/index`, watchIndex);
    yield debounce(100, `${prefix ? `${prefix}/` : ''}sms/retrieve`, watchRetrieve);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}sms/loadParams`, watchLoadParams);
    yield takeEvery(`${prefix ? `${prefix}/` : ''}sms/setParams`, watchSetParams);
  }

  return watchSmsSagas;
}
